import React, { useState } from "react";
import { Navigation } from "@/components/Navigation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts/AuthContext";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { User } from "lucide-react";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
    AlertDialogAction,
} from "@/components/ui/alert-dialog";

const Profile = () => {
    const { toast } = useToast();
    const { authFetch, user, setUser } = useAuth();
    const [showPasswordCard, setShowPasswordCard] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const { t } = useTranslation();

    const profileSchema = z.object({
        name: z.string().optional(),
        email: z.string().email(t("profile.invalidEmail")),
        profilePicture: z.string().optional(),
    });

    const passwordSchema = z
        .object({
            currentPassword: z.string().min(6, t("profile.password.min")),
            newPassword: z.string().min(6, t("profile.password.min")),
            confirmPassword: z.string().min(6, t("profile.password.min")),
        })
        .refine((data) => data.newPassword === data.confirmPassword, {
            message: t("profile.password.match"),
            path: ["confirmPassword"],
        });

    type ProfileFormData = z.infer<typeof profileSchema>;
    type PasswordFormData = z.infer<typeof passwordSchema>;

    const profileForm = useForm<ProfileFormData>({
        resolver: zodResolver(profileSchema),
        defaultValues: {
            name: user.name || "",
            email: user.email || "",
        },
    });

    const passwordForm = useForm<PasswordFormData>({
        resolver: zodResolver(passwordSchema),
        defaultValues: {
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
        },
    });

    const onSubmitProfile = async (data: ProfileFormData) => {
        try {
            const updateData = {
                ...(data.name !== user.name && { name: data.name }),
                ...(data.email !== user.email && { email: data.email }),
            };

            if (Object.keys(updateData).length === 0) return;

            const response = await authFetch("/api/user/update", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(updateData),
            });

            if (response.status !== 200) {
                throw new Error(t("profile.toasts.profileUpdateError"));
            }

            toast({
                title: t("profile.toasts.profileUpdated"),
            });

            setUser({ ...user, ...updateData });
        } catch (error) {
            toast({
                variant: "destructive",
                title: t("profile.toasts.profileUpdateError"),
                description: error instanceof Error ? error.message : t("profile.toasts.genericError"),
            });
        }
    };

    const onSubmitPassword = async (data: PasswordFormData) => {
        try {
            const response = await authFetch("/api/user/change-password", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    currentPassword: data.currentPassword,
                    newPassword: data.newPassword,
                }),
            });

            if (response.status !== 200) {
                throw new Error(t("profile.toasts.passwordUpdateError"));
            }

            toast({
                title: t("profile.toasts.passwordUpdated"),
            });

            setShowPasswordCard(false);
        } catch (error) {
            toast({
                variant: "destructive",
                title: t("profile.toasts.passwordUpdateError"),
                description: error instanceof Error ? error.message : t("profile.toasts.genericError"),
            });
        }
    };

    const onPictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                const img = new Image();
                let isUpdating = true; // Variabile di controllo per prevenire cicli
                img.src = reader.result as string;
                img.onload = async () => {
                    if (!isUpdating) return; // Esci se l'immagine non deve essere aggiornata

                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");
                    canvas.width = 512;
                    canvas.height = 512;
                    ctx?.drawImage(img, 0, 0, 512, 512);
                    const dataUrl = canvas.toDataURL("image/jpeg");
                    profileForm.setValue("profilePicture", dataUrl);

                    // Aggiorna il server
                    try {
                        const response = await authFetch("/api/user/update", {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify({ profilePicture: dataUrl }),
                        });

                        if (response.status === 200) {
                            setUser({ ...user, picture: dataUrl });
                        } else {
                            throw new Error();
                        }
                    } catch {
                        toast({
                            variant: "destructive",
                            title: t("profile.toasts.profileUpdateError"),
                        });

                        // Ripristina immagine precedente
                        isUpdating = false; // Evita che `onload` venga richiamato di nuovo
                        img.src = user.picture || "";
                    }
                };
            };
            reader.readAsDataURL(file);
        }
    };

    const deleteAccount = async () => {
        try {
            const response = await authFetch("/api/user/delete", {
                method: "DELETE",
            });

            if (!response.ok) {
                throw new Error(t("profile.toasts.accountDeleteError"));
            }

            toast({
                title: t("profile.toasts.accountDeleted"),
            });

            // Redirect or perform additional actions after account deletion
        } catch (error) {
            toast({
                variant: "destructive",
                title: t("profile.toasts.accountDeleteError"),
                description: error instanceof Error ? error.message : t("profile.toasts.genericError"),
            });
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Navigation />

            <div className="pt-24 pb-6 px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>{t("profile.title")}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center space-x-12">
                                {/* Profile Picture */}
                                <div className="flex-shrink-0 flex flex-col items-center space-y-4">
                                    <div className="w-32 h-32 rounded-full bg-gray-200 overflow-hidden">
                                        {user.picture ? (
                                            <img src={user.picture} alt="Profile" className="object-cover w-full h-full" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                <User className="h-12 w-12" />
                                            </div>
                                        )}
                                    </div>
                                    <input id="profilePictureInput" type="file" accept="image/*" className="hidden" onChange={onPictureChange} />
                                    <Button
                                        variant="link"
                                        className="text-blue-600"
                                        onClick={() => {
                                            document.getElementById("profilePictureInput")?.click();
                                        }}
                                    >
                                        {t("profile.changePicture")}
                                    </Button>
                                </div>

                                <div className="flex flex-col space-y-4 flex-1">
                                    <Form {...profileForm}>
                                        <form onSubmit={profileForm.handleSubmit(onSubmitProfile)} className="space-y-6">
                                            <FormField
                                                control={profileForm.control}
                                                name="name"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>{t("profile.name")}</FormLabel>
                                                        <FormControl>
                                                            <Input {...field} placeholder={t("profile.namePlaceholder")} />
                                                        </FormControl>
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={profileForm.control}
                                                name="email"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>{t("profile.email")}</FormLabel>
                                                        <FormControl>
                                                            <Input {...field} placeholder={t("profile.emailPlaceholder")} type="email" />
                                                        </FormControl>
                                                    </FormItem>
                                                )}
                                            />

                                            <Button type="submit" className="w-full">
                                                {t("profile.saveChanges")}
                                            </Button>
                                        </form>
                                    </Form>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {!showPasswordCard && (
                        <Card>
                            <Button variant="link" className="text-blue-600" onClick={() => setShowPasswordCard(!showPasswordCard)}>
                                {t("profile.changePassword")}
                            </Button>

                            <Button variant="link" className="text-blue-600" onClick={() => setShowConfirmDialog(true)}>
                                {t("profile.delete.button")}
                            </Button>
                        </Card>
                    )}
                    {showPasswordCard && (
                        <Card>
                            <CardHeader>
                                <CardTitle>{t("profile.password.title")}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Form {...passwordForm}>
                                    <form onSubmit={passwordForm.handleSubmit(onSubmitPassword)} className="space-y-6">
                                        <FormField
                                            control={passwordForm.control}
                                            name="currentPassword"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t("profile.password.current")}</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} type="password" placeholder={t("profile.password.currentPlaceholder")} />
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={passwordForm.control}
                                            name="newPassword"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t("profile.password.new")}</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} type="password" placeholder={t("profile.password.newPlaceholder")} />
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={passwordForm.control}
                                            name="confirmPassword"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t("profile.password.confirm")}</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} type="password" placeholder={t("profile.password.confirmPlaceholder")} />
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />
                                        <Button type="submit" className="w-full">
                                            {t("profile.password.update")}
                                        </Button>
                                    </form>
                                </Form>
                            </CardContent>
                        </Card>
                    )}

                    <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>{t("profile.delete.dialog.title")}</AlertDialogTitle>
                                <AlertDialogDescription>{t("profile.delete.dialog.description")}</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel onClick={() => setShowConfirmDialog(false)}>{t("profile.delete.dialog.cancel")}</AlertDialogCancel>
                                <AlertDialogAction onClick={deleteAccount}>{t("profile.delete.dialog.confirm")}</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>
        </div>
    );
};

export default Profile;
